import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { Keyboard, X } from 'lucide-react';
import { axiomTheme } from '../styles/designTokens';
import { shortcutHelpGroups } from '../utils/operatorCommandRegistry';

interface KeyboardShortcutsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(10px);
  padding: 10vh 1.25rem 2rem;
`;

const Dialog = styled(motion.div)`
  width: min(980px, 100%);
  max-height: 82vh;
  overflow: hidden;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.hero};
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.98));
  box-shadow: ${axiomTheme.shadow.drawer};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${axiomTheme.color.text.primary};
  font-size: 1rem;
  font-weight: 850;
`;

const HeaderCopy = styled.div`
  margin-top: 0.25rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.78rem;
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${axiomTheme.radius.md};
  border: 1px solid ${axiomTheme.color.border.subtle};
  background: rgba(15, 23, 42, 0.72);
  color: ${axiomTheme.color.text.muted};
  cursor: pointer;

  &:hover {
    color: ${axiomTheme.color.text.primary};
    border-color: ${axiomTheme.color.border.strong};
  }
`;

const Body = styled.div`
  max-height: calc(82vh - 80px);
  overflow-y: auto;
  padding: 1.15rem;
`;

const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }
`;

const Group = styled.section`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(15, 23, 42, 0.52);
  overflow: hidden;
`;

const GroupTitle = styled.div`
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  color: ${axiomTheme.color.text.primary};
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const ShortcutRow = styled.div`
  display: grid;
  grid-template-columns: minmax(145px, 0.62fr) minmax(0, 1fr);
  gap: 0.8rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const Keys = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
`;

const Key = styled.kbd`
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.16rem 0.45rem;
  border-radius: ${axiomTheme.radius.xs};
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(2, 6, 23, 0.52);
  color: ${axiomTheme.color.text.secondary};
  font-size: 0.68rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 750;
`;

const Action = styled.div`
  color: ${axiomTheme.color.text.secondary};
  font-size: 0.78rem;
  font-weight: 700;
`;

const Note = styled.div`
  margin-top: 0.18rem;
  color: ${axiomTheme.color.text.faint};
  font-size: 0.68rem;
  font-weight: 600;
`;

const KeyboardShortcutsOverlay: React.FC<KeyboardShortcutsOverlayProps> = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Dialog
          initial={{ opacity: 0, y: -18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -18, scale: 0.98 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          onClick={event => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
        >
          <Header>
            <div>
              <HeaderTitle>
                <Keyboard size={18} />
                Keyboard Shortcuts
              </HeaderTitle>
              <HeaderCopy>Terminal-grade navigation and ticket workflow commands for PM and trader workflows.</HeaderCopy>
            </div>
            <CloseButton onClick={onClose} aria-label="Close keyboard shortcuts">
              <X size={17} />
            </CloseButton>
          </Header>
          <Body>
            <GroupGrid>
              {shortcutHelpGroups.map(group => (
                <Group key={group.title}>
                  <GroupTitle>{group.title}</GroupTitle>
                  {group.items.map(item => (
                    <ShortcutRow key={`${group.title}-${item.keys.join('-')}-${item.action}`}>
                      <Keys>
                        {item.keys.map(key => (
                          <Key key={key}>{key}</Key>
                        ))}
                      </Keys>
                      <div>
                        <Action>{item.action}</Action>
                        {item.note && <Note>{item.note}</Note>}
                      </div>
                    </ShortcutRow>
                  ))}
                </Group>
              ))}
            </GroupGrid>
          </Body>
        </Dialog>
      </Overlay>
    )}
  </AnimatePresence>
);

export default KeyboardShortcutsOverlay;
